import { useState } from 'react';

export type chatModeType = 'thinking' | 'comparison' | 'standard' | 'chat_only';

const STORAGE_KEY_PREFIX = 'chatMode_inquiry_';

const getPersistedMode = (inquiryId: string | null): chatModeType | null => {
  if (!inquiryId) return null;
  try {
    const stored = localStorage.getItem(`${STORAGE_KEY_PREFIX}${inquiryId}`);
    if (
      stored === 'thinking' ||
      stored === 'comparison' ||
      stored === 'standard' ||
      stored === 'chat_only'
    ) {
      return stored as chatModeType;
    }
  } catch {
    // localStorage not available
  }
  return null;
};

const persistMode = (inquiryId: string | null, mode: chatModeType) => {
  if (!inquiryId) return;
  try {
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${inquiryId}`, mode);
  } catch {
    // localStorage not available
  }
};

export const useHandleChatMode = ({
  defaultChatMode,
}: {
  defaultChatMode: chatModeType;
}) => {
  const [chatMode, setThinkingMode] = useState<chatModeType>(defaultChatMode);

  /**
   * Called by the parent component after resolving the correct mode for the
   * current inquiry (checking localStorage first, then the API's chat_mode).
   * Does NOT touch localStorage — the value may come from the server.
   */
  const applyMode = (mode: chatModeType) => {
    setThinkingMode(mode);
  };

  /**
   * Called on explicit user interaction with a mode switch.
   * Persists the choice to localStorage so it survives reloads and chat switches.
   */
  const handleSetChatMode = (mode: chatModeType, inquiryId?: string | null) => {
    const resolved: chatModeType =
      mode === 'thinking' || mode === 'comparison' || mode === 'chat_only'
        ? mode
        : 'standard';
    setThinkingMode(resolved);
    persistMode(inquiryId ?? null, resolved);
  };

  return {
    chatMode,
    applyMode,
    getPersistedMode,
    persistMode,
    setThinkingMode,
    handleSetChatMode,
  };
};
